import { OverlayRef } from '@angular/cdk/overlay';
import { Observable, Subject } from 'rxjs';

import { NdtSnackBarContainerBase } from './snack-bar-container';

/** Event that is emitted when a snack bar is dismissed. */
export interface NdtSnackBarDismiss {
  /** Whether the snack bar was dismissed using the action button. */
  dismissedByAction: boolean;
}

/** Maximum amount of milliseconds that can be passed into setTimeout. */
const MAX_TIMEOUT = 10000;

/**
 * Reference to a snack bar dispatched from the snack bar service.
 */
export class NdtSnackBarRef<T> {
  /** The instance of the component making up the content of the snack bar. */
  instance: T;

  /**
   * The instance of the component making up the content of the snack bar.
   * @docs-private
   */
  containerInstance: NdtSnackBarContainerBase;

  /** Subject for notifying the user that the snack bar has been dismissed. */
  private readonly _afterDismissed = new Subject<NdtSnackBarDismiss>();

  /** Subject for notifying the user that the snack bar has opened and appeared. */
  private readonly _afterOpened = new Subject<void>();

  /** Subject for notifying the user that the snack bar action was called. */
  private readonly _onAction = new Subject<void>();

  /**
   * Timeout ID for the duration setTimeout call. Used to clear the timeout if the snackbar is
   * dismissed before the duration passes.
   */
  private durationTimeoutId: number;
  private durationLeft: number | null = null;
  private timeoutStart: number | null = null;
  /** Whether the snack bar was dismissed using the action button. */
  private dismissedByAction = false;

  constructor(containerInstance: NdtSnackBarContainerBase, private readonly overlayRef: OverlayRef) {
    this.containerInstance = containerInstance;
    containerInstance.mouseEnter.subscribe(() => {
      this.pauseTimeout();
    });
    containerInstance.mouseOut.subscribe(() => {
      this.resumeTimeout();
    });
    containerInstance._exit.subscribe(() => this.finishDismiss());
  }

  /** Dismisses the snack bar. */
  dismiss(): void {
    if (!this._afterDismissed.closed) {
      this.containerInstance.exit();
    }
    this.clearDuration();
  }

  /** Marks the snackbar action clicked. */
  dismissWithAction(): void {
    if (!this._onAction.closed) {
      this.dismissedByAction = true;
      this._onAction.next();
      this._onAction.complete();
      this.dismiss();
    }
    this.clearDuration();
  }

  /**
   * Dismisses the snack bar after some duration
   * @param duration
   */
  dismissAfter(duration: number): void {
    this.durationTimeoutId = setTimeout(() => this.dismiss(), Math.min(duration, MAX_TIMEOUT));
    this.timeoutStart = Date.now();
  }

  /** Marks the snackbar as opened */
  open(): void {
    if (!this._afterOpened.closed) {
      this._afterOpened.next();
      this._afterOpened.complete();
    }
  }

  pauseTimeout(): void {
    clearTimeout(this.durationTimeoutId);
    const elapsed = Date.now() - this.timeoutStart;
    this.durationLeft = (this.durationLeft ?? this.containerInstance.snackBarConfig.duration) - elapsed;
  }

  resumeTimeout(): void {
    this.dismissAfter(this.durationLeft);
  }

  /** Gets an observable that is notified when the snack bar is finished closing. */
  afterDismissed(): Observable<NdtSnackBarDismiss> {
    return this._afterDismissed;
  }

  /** Gets an observable that is notified when the snack bar has opened and appeared. */
  afterOpened(): Observable<void> {
    return this.containerInstance.onEnter;
  }

  /** Gets an observable that is notified when the snack bar action is called. */
  onAction(): Observable<void> {
    return this._onAction;
  }

  private clearDuration(): void {
    this.timeoutStart = null;
    clearTimeout(this.durationTimeoutId);
  }

  /** Cleans up the DOM after closing. */
  private finishDismiss(): void {
    this.overlayRef.dispose();

    if (!this._onAction.closed) {
      this._onAction.complete();
    }

    this._afterDismissed.next({ dismissedByAction: this.dismissedByAction });
    this._afterDismissed.complete();
    this.dismissedByAction = false;
  }
}
